
import _ from 'lodash'
import { groupCarrierVariants } from '~/util/plan-name'
import { planAka, planDisplayName } from '~/util/plan-notes'

const ALL_TYPES = 'mapd,ma,pdp,supp'
// oracle's /plan_search/ MAX_LIMIT; a carrier with more plans than this in a
// year (a national MAPD carrier with no state picked) gets a server search.
const PLAN_LIMIT = 200
// loadPlans' "pick the `planId` prop as it is when the load lands", so a plan
// the page sets mid-load (Show notes, a shared link) is not lost.
const FROM_PROP = 'from-prop'

// Carrier, then plan, from the year's catalog: the same grouped carrier list
// as the current-plan step (one name per discount group, the plan search
// fanned out to every variant behind it). Emits `change` with the resolved
// { carrier: { name, variants }, plan } once its lists load and on each pick.
export default {
    props: {
        year: { type: Number, required: true },
        planType: { type: String, default: null },
        state: { type: String, default: null },
        // A carrier group name to start from (e.g. from the URL).
        carrierName: { type: String, default: null },
        // A plan id to start from; kept as "Plan #id" if the list lacks it.
        planId: { type: Number, default: null },
        requirePlan: { type: Boolean, default: false },
    },
    data () {
        return {
            carrierRows: [],
            planRows: [],
            loadingCarriers: false,
            loadingPlans: false,
            selectedCarrier: null,
            selectedPlan: null,
            // The page set `planId` while plans were loading.
            pickFromProp: false,
            truncated: false,
            planLimit: PLAN_LIMIT,
        }
    },
    computed: {
        carrierGroups () { return groupCarrierVariants(this.carrierRows) },
        planOptions () {
            return this.planRows.map(p => ({ ...p, label: planDisplayName(p), aka: planAka(p) }))
        },
        planPlaceholder () {
            if (!this.selectedCarrier) { return 'Choose a carrier first' }
            if (this.loadingPlans) { return 'Loading plans…' }
            return this.requirePlan ? 'Pick a plan' : 'All plans'
        },
        types () { return this.planType || ALL_TYPES },
        scopeKey () { return [this.year, this.types, this.state].join('|') },
    },
    watch: {
        // Plan ids are per year, so a new scope keeps the carrier only.
        scopeKey () { this.loadCarriers(null) },
        carrierName (name) {
            if (name === (this.selectedCarrier?.name || null)) { return }
            this.selectedCarrier = this.findGroup(name)
            this.loadPlans(FROM_PROP)
        },
        planId (id) {
            if (id === (this.selectedPlan?.id ?? null)) { return }
            // Mid-load, the load that lands picks it up.
            if (this.loadingPlans) {
                this.pickFromProp = true
                return
            }
            this.selectedPlan = this.findPlan(id)
            this.emit()
        },
    },
    mounted () {
        this.loadCarriers(FROM_PROP)
    },
    created () {
        // Request counters: only the newest load's answer is kept, so a slow
        // one for a carrier or year the agent already left never lands.
        this.seq = { carriers: 0, plans: 0 }
        this.onPlanSearch = _.debounce(text => this.searchServer(text), 300)
    },
    methods: {
        findGroup (name) {
            if (!name) { return null }
            return this.carrierGroups.find(g => g.name === name) || { name, variants: [name] }
        },
        findPlan (id) {
            if (!id) { return null }
            return this.planOptions.find(p => p.id === id) || { id, label: `Plan #${id}`, aka: '' }
        },
        async loadCarriers (planId) {
            const seq = ++this.seq.carriers
            this.loadingCarriers = true
            const resp = await this.$try('searchPlanCarriers', {
                year: this.year,
                types: this.types,
                state: this.state || undefined,
            }, { spinner: false })
            if (seq !== this.seq.carriers) { return }
            this.carrierRows = resp?.results || []
            this.loadingCarriers = false
            this.selectedCarrier = this.findGroup(this.selectedCarrier?.name || this.carrierName)
            await this.loadPlans(planId)
        },
        // `planId`: undefined keeps the current pick, FROM_PROP takes the
        // prop when the load lands, else the id (or null) to select. `q` is a
        // server search within a capped list, and leaves `truncated` as the
        // full load set it.
        async loadPlans (planId, q) {
            const seq = ++this.seq.plans
            this.planRows = []
            if (!q) { this.truncated = false }
            if (!this.selectedCarrier) {
                this.loadingPlans = false
                this.selectedPlan = null
                this.emit()
                return
            }
            this.loadingPlans = true
            const resps = await Promise.all(this.selectedCarrier.variants.map(carrier => this.$try('searchPlans', {
                year: this.year,
                types: this.types,
                state: this.state || undefined,
                carrier,
                limit: PLAN_LIMIT,
                ...(q ? { q } : {}),
            }, { spinner: false })))
            if (seq !== this.seq.plans) { return }
            const seen = new Set()
            this.planRows = resps
                .flatMap(r => r?.results || [])
                .filter(p => !seen.has(p.id) && seen.add(p.id))
            if (!q) { this.truncated = resps.some(r => (r?.results || []).length >= PLAN_LIMIT) }
            this.loadingPlans = false
            const pick = planId === FROM_PROP || this.pickFromProp ? this.planId : planId
            this.pickFromProp = false
            if (pick !== undefined) {
                this.selectedPlan = this.findPlan(pick)
            }
            this.emit()
        },
        // Typing in a capped list asks the server (keeping the current pick);
        // a list that fit whole is filtered by Multiselect itself. Clearing
        // the text reloads the full, capped list.
        searchServer (text) {
            if (!this.truncated) { return }
            return this.loadPlans(undefined, (text || '').trim() || undefined)
        },
        pickCarrier (group) {
            this.selectedCarrier = group
            this.selectedPlan = null
            this.pickFromProp = false
            this.loadPlans(null)
        },
        pickPlan (plan) {
            this.selectedPlan = plan
            this.emit()
        },
        emit () {
            this.$emit('change', { carrier: this.selectedCarrier, plan: this.selectedPlan })
        },
    },
}
